import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  output,
  signal
} from "@angular/core";
import { Bind } from "src/app/support/decorators/bind";
import { blobToFile } from "src/app/support/file";
import { ReportDesignerItemImageValue } from "../../report-design-item";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { lastValueFrom, of } from "rxjs";
import { tap } from "rxjs/operators";
import { blobToDataUrl, dataUrlToBlob } from "src/app/support/blob";
import { nextRuntimeId } from "src/app/support/runtime-id";
import { toString } from "lodash-es";
import { scaleImage } from "src/app/support/image";
import { ValueChangedEvent } from "devextreme/ui/text_box";
import { tryParseJson } from "src/app/support/json";

export type ReportFormRendererImageComponentValueType = {
  storageId: string;
  fileName: string;
  fileSize: number;
  caption: string;
  align: "left" | "center" | "right";
  size: number;
};

@Component({
  selector: "snt-report-form-renderer-image",
  templateUrl: "report-form-renderer-image.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class ReportFormRendererImageComponent implements OnInit {
  @Input()
  item!: ReportDesignerItemImageValue;

  @Input()
  level!: number;

  @Input()
  parentName!: string;

  @Input()
  readOnly: boolean = false;

  @Output()
  value: EventEmitter<void> = new EventEmitter();

  align = signal<"left" | "center" | "right">("left");
  size = signal<number>(3);
  caption = signal<string>("");

  imageData: string | undefined;

  initialFile: { name: string; size: number } | undefined = undefined;

  uploadStarted = output();
  uploadEnded = output();

  private readonly storageBackend = inject(StorageBackend);

  ngOnInit(): void {
    if (this.item.config?.caption) {
      this.caption.set(this.item.config.caption);
    }

    if (this.item.config?.align) {
      this.align.set(this.item.config.align);
    }

    if (this.item.value) {
      try {
        const parsedValue =
          tryParseJson<ReportFormRendererImageComponentValueType>(
            this.item.value,
            {
              caption: "",
              fileName: "",
              fileSize: 0,
              storageId: "",
              align: "left",
              size: 3
            }
          );
        if (parsedValue) {
          if (parsedValue.caption) {
            this.caption.set(parsedValue.caption);
          }

          if (parsedValue.align) {
            this.align.set(parsedValue.align);
          }

          if (parsedValue.size) {
            this.size.set(parsedValue.size);
          }

          if (parsedValue.storageId) {
            this.initialFile = {
              name: parsedValue.fileName,
              size: parsedValue.fileSize
            };

            lastValueFrom(this.storageBackend.download(parsedValue.storageId))
              .then((imageUrl) => {
                this.imageData = imageUrl;
              })
              .catch();
          }
        }
      } catch (ex) {}
    }
  }

  onAlignChanged(align: "left" | "center" | "right"): void {
    this.align.set(align);

    const actualValue = tryParseJson<ReportFormRendererImageComponentValueType>(
      this.item.value,
      {
        caption: "",
        align: "left",
        fileName: "",
        fileSize: 0,
        storageId: "",
        size: 3
      }
    );

    this.item.value = JSON.stringify({
      ...actualValue,
      align
    });

    this.value.emit();
  }

  onSizeChanged(size: number): void {
    this.size.set(size);

    const actualValue = tryParseJson<ReportFormRendererImageComponentValueType>(
      this.item.value,
      {
        caption: "",
        align: "left",
        fileName: "",
        fileSize: 0,
        storageId: "",
        size: 2
      }
    );

    this.item.value = JSON.stringify({
      ...actualValue,
      size
    });

    this.value.emit();
  }

  onCaptionValueChanged(dxEvent: ValueChangedEvent): void {
    const actualValue = tryParseJson<ReportFormRendererImageComponentValueType>(
      this.item.value,
      {
        caption: "",
        fileName: "",
        fileSize: 0,
        storageId: "",
        align: "left",
        size: 3
      }
    );

    this.item.value = JSON.stringify({
      ...actualValue,
      caption: dxEvent.value
    });

    this.value.emit();
  }

  async renderPdfObject(): Promise<Record<string, any>> {
    if (!this.item.value) {
      return {
        type: "image",
        config: {
          imageData: ""
        }
      };
    }

    try {
      const parsedValue: ReportFormRendererImageComponentValueType = JSON.parse(
        this.item.value
      );

      const imageUrl = await lastValueFrom(
        this.storageBackend.download(parsedValue.storageId)
      );

      const imageBlob = await (await fetch(imageUrl)).blob();
      const dataUrl = await blobToDataUrl(imageBlob);

      return {
        type: "image",
        config: {
          imageData: dataUrl
        }
      };
    } catch (ex) {
      return {
        type: "image",
        config: {
          imageData: ""
        }
      };
    }
  }

  @Bind
  onRemoveImage(): void {
    if (!this.item.value) {
      return;
    }

    const parsedValue: ReportFormRendererImageComponentValueType = JSON.parse(
      this.item.value
    );

    lastValueFrom(this.storageBackend.delete(parsedValue.storageId)).catch();

    this.imageData = undefined;
    this.item.value = "";

    this.value.emit();
  }

  @Bind
  async onProcessFile(
    fieldName: string,
    file: any,
    metadata: Record<string, any>,
    load: (id: string) => void,
    error: (error: string) => void,
    progress: (
      isLengthComputable: boolean,
      loadedDataAmount: number,
      totalDataAmount: number
    ) => void
  ): Promise<void> {
    this.uploadStarted.emit();

    const resizedDataUrl = await scaleImage(file);

    this.imageData = resizedDataUrl;
    const resizedBlob = await dataUrlToBlob(resizedDataUrl);
    const resizedFile = blobToFile(resizedBlob, file.name);

    try {
      await lastValueFrom(
        this.storageBackend
          .upload(resizedFile, (storageId) => {
            const actualValue =
              tryParseJson<ReportFormRendererImageComponentValueType>(
                this.item.value,
                {
                  caption: "",
                  fileName: "",
                  fileSize: 0,
                  storageId: "",
                  align: "left",
                  size: 2
                }
              );

            this.item.value = JSON.stringify(<
              ReportFormRendererImageComponentValueType
            >{
              ...actualValue,
              storageId,
              fileName: file.name,
              fileSize: resizedFile.size
            });
            this.value.emit();
            return of(null);
          })
          .pipe(
            tap((status) => {
              progress(true, status.loaded, status.total);
            })
          )
      );

      load(nextRuntimeId());

      this.uploadEnded.emit();
    } catch (ex) {
      this.uploadEnded.emit();

      error(toString(ex));
    }
  }
}
